import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ddtApi, clientsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ClientSearch from '../components/ClientSearch';
import { ArrowLeft, FilePlus2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DDTCreatePage — NUOVO FLUSSO (Giugno 2026):
 *   1. Mostra solo il selettore cliente.
 *   2. Appena l'utente sceglie il cliente, il DDT viene SUBITO creato sul backend
 *      con status="confirmed" e numero progressivo. NIENTE più bozze.
 *   3. L'utente viene portato a `/ddt/{id}/edit` per aggiungere i prodotti, che vengono
 *      poi salvati con autosave nel form di modifica.
 *
 * AUTH GATE (Luglio 2026): all'ingresso della pagina e prima di creare il DDT chiamiamo
 * GET /api/auth/me. Se la sessione è scaduta (401/403) mostriamo un toast e riportiamo
 * subito l'utente al login PRIMA che scriva qualcosa che poi non riuscirebbe a salvare.
 */
const DDTCreatePage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');

  // Se la sessione è scaduta: mostra toast, logout, redirect a /login.
  // Ritorna true se OK, false se scaduta.
  const guardSession = async () => {
    try {
      await axios.get(`${API}/auth/me`);
      return true;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Sessione scaduta: accedi di nuovo prima di creare il DDT', { duration: 4000 });
        logout();
        navigate('/login', { replace: true, state: { from: '/ddt/new' } });
        return false;
      }
      // Errore di rete transitorio: non blocchiamo
      console.warn('[DDTCreatePage] verifica sessione fallita (rete):', err?.message);
      return true;
    }
  };

  useEffect(() => {
    (async () => {
      const ok = await guardSession();
      if (!ok) { setLoading(false); return; }
      try {
        const res = await clientsApi.getAll();
        setClients(res.data);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          toast.error('Sessione scaduta: accedi di nuovo prima di creare il DDT', { duration: 4000 });
          logout();
          navigate('/login', { replace: true, state: { from: '/ddt/new' } });
        } else {
          toast.error('Errore nel caricamento dei clienti');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const selectedClient = clients.find(c => c.id === selectedClientId);

  const createAndOpen = async () => {
    if (!selectedClient) {
      toast.error('Seleziona un cliente per creare il DDT');
      return;
    }
    setCreating(true);
    // Doppia verifica: la sessione potrebbe essere scaduta tra il mount e il click
    const ok = await guardSession();
    if (!ok) { setCreating(false); return; }
    try {
      const today = new Date().toISOString().split('T')[0];
      const payload = {
        client_id: selectedClient.id,
        client_name: (selectedClient.nickname && selectedClient.nickname.trim())
          ? selectedClient.nickname
          : selectedClient.name,
        client_legal_name: selectedClient.name,
        client_address: selectedClient.address || '',
        client_city: selectedClient.city || '',
        client_cap: selectedClient.cap || '',
        client_piva: selectedClient.piva || '',
        listino: '1',
        items: [],
        total_boxes: 0, total_pieces: 0,
        total_amount: 0, total_cost: 0, total_margin: 0,
        paid_amount: 0, previous_balance: 0,
        aspetto_beni: 'A VISTA',
        porto: 'Franco',
        transport_date: today,
        transport_time: '',
        vettore: '',
        note: '',
        internal_ref: '',
      };
      const res = await ddtApi.create(payload);
      const newId = res.data?.id;
      toast.success(`DDT n° ${res.data.number}/${res.data.year} creato — aggiungi i prodotti`);
      navigate(`/ddt/${newId}/edit`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Sessione scaduta durante la creazione: accedi di nuovo', { duration: 4000 });
        logout();
        navigate('/login', { replace: true, state: { from: '/ddt/new' } });
      } else {
        toast.error('Errore nella creazione del DDT');
      }
      setCreating(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/ddt')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Indietro
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-['Manrope'] flex items-center gap-2">
            <FilePlus2 className="w-5 h-5 text-emerald-600" />
            Nuovo DDT
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Seleziona il cliente: il DDT verrà creato subito e numerato. Poi potrai aggiungere i prodotti.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Caricamento clienti…</div>
          ) : (
            <>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">
                  Cliente
                </label>
                <ClientSearch
                  clients={clients}
                  value={selectedClientId}
                  onChange={setSelectedClientId}
                  placeholder="Cerca cliente per nome…"
                />
              </div>

              {selectedClient && (
                <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3 text-sm" data-testid="selected-client-card">
                  <div className="font-semibold text-blue-900">{selectedClient.nickname || selectedClient.name}</div>
                  {selectedClient.name && selectedClient.nickname && selectedClient.nickname !== selectedClient.name && (
                    <div className="text-xs italic text-blue-800/70">{selectedClient.name}</div>
                  )}
                  <div className="text-xs text-muted-foreground mt-1">
                    {[selectedClient.address, selectedClient.cap, selectedClient.city].filter(Boolean).join(' · ') || '—'}
                    {selectedClient.piva ? ` · P.IVA ${selectedClient.piva}` : ''}
                  </div>
                </div>
              )}

              <Button
                type="button"
                data-testid="create-ddt-btn"
                onClick={createAndOpen}
                disabled={!selectedClient || creating}
                className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creazione DDT…
                  </>
                ) : (
                  <>
                    <FilePlus2 className="w-4 h-4 mr-2" />
                    Crea DDT e aggiungi prodotti
                  </>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DDTCreatePage;
